import {
	FORMULA_OPERATIONS as FP,
	GeometryTypes,
	PathOperationTypes,
	ShapeType,
} from "../widgets/interfaces/model/enumerations/index";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";

/**
 * pentagon preset definition
 * ECMA-376, Third Edition, Part 1 - Fundamentals And Markup Language Reference
 * /OfficeOpenXML-DrawingMLGeometries/presetShapeDefinitions.xml
 */

export const pentagon: IPresetGeometry = {
	avList: [
		{ name: "hf", fmla: { operation: FP.LiteralValue, operands: [105146] } },
		{ name: "vf", fmla: { operation: FP.LiteralValue, operands: [110557] } },
	],

	gdLst: [
		{ name: "swd2", fmla: { operation: FP.MultiplyDivide, operands: ["wd2", "hf", 100000] } },
		{ name: "shd2", fmla: { operation: FP.MultiplyDivide, operands: ["hd2", "vf", 100000] } },
		{ name: "svc", fmla: { operation: FP.MultiplyDivide, operands: ["vc", "vf", 100000] } },
		// { name: "dx1", fmla: { operation: FP.Cosine, operands: ["swd2", 1080000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dx1", fmla: { operation: FP.Cosine, operands: ["swd2", 18] } },
		// { name: "dx2", fmla: { operation: FP.Cosine, operands: ["swd2", 18360000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dx2", fmla: { operation: FP.Cosine, operands: ["swd2", 306] } },
		// { name: "dy1", fmla: { operation: FP.Sine, operands: ["shd2", 1080000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dy1", fmla: { operation: FP.Sine, operands: ["shd2", 18] } },
		// { name: "dy2", fmla: { operation: FP.Sine, operands: ["shd2", 18360000] } },
		// instead of microsoft version of angle metric, use degrees
		{ name: "dy2", fmla: { operation: FP.Sine, operands: ["shd2", 306] } },
		{ name: "x1", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "dx1"] } },
		{ name: "x2", fmla: { operation: FP.AddSubtract, operands: ["hc", 0, "dx2"] } },
		{ name: "x3", fmla: { operation: FP.AddSubtract, operands: ["hc", "dx2", 0] } },
		{ name: "x4", fmla: { operation: FP.AddSubtract, operands: ["hc", "dx1", 0] } },
		{ name: "y1", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "dy1"] } },
		{ name: "y2", fmla: { operation: FP.AddSubtract, operands: ["svc", 0, "dy2"] } },
		{ name: "it", fmla: { operation: FP.MultiplyDivide, operands: ["y1", "dx2", "dx1"] } },
	],

	pathLst: [{
		operations: [
			{ pt: { x: "x1", y: "y1" }, type: PathOperationTypes.MOVE_TO },
			{ pt: { x: "hc", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x4", y: "y1" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x3", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x2", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ type: PathOperationTypes.CLOSE },
		],
	}],
	presetShape: ShapeType.pentagon,
	rect: { l: "x2", t: "it", r: "x3", b: "y2" },
	type: GeometryTypes.PRESET,
};

/**
 * Microsoft open xml preset definition (for Reference purpose only)
 * <pentagon>
 *
 * <avLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *   <gd name="hf" fmla="val 105146" />
 *   <gd name="vf" fmla="val 110557" />
 * </avLst>
 *
 * <gdLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *   <gd name="swd2" fmla="* / wd2 hf 100000" />
 *   <gd name="shd2" fmla="* / hd2 vf 100000" />
 *   <gd name="svc" fmla="* / vc  vf 100000" />
 *   <gd name="dx1" fmla="cos swd2 1080000" />
 *   <gd name="dx2" fmla="cos swd2 18360000" />
 *   <gd name="dy1" fmla="sin shd2 1080000" />
 *   <gd name="dy2" fmla="sin shd2 18360000" />
 *   <gd name="x1" fmla="+- hc 0 dx1" />
 *   <gd name="x2" fmla="+- hc 0 dx2" />
 *   <gd name="x3" fmla="+- hc dx2 0" />
 *   <gd name="x4" fmla="+- hc dx1 0" />
 *   <gd name="y1" fmla="+- svc 0 dy1" />
 *   <gd name="y2" fmla="+- svc 0 dy2" />
 *   <gd name="it" fmla="* / y1 dx2 dx1" />
 * </gdLst>
 *
 * <cxnLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *   <cxn ang="3cd4">
 *     <pos x="hc" y="t" />
 *   </cxn>
 *   <cxn ang="cd2">
 *     <pos x="x1" y="y1" />
 *   </cxn>
 *   <cxn ang="cd4">
 *     <pos x="x2" y="y2" />
 *   </cxn>
 *   <cxn ang="cd4">
 *     <pos x="hc" y="b" />
 *   </cxn>
 *   <cxn ang="cd4">
 *     <pos x="x3" y="y2" />
 *   </cxn>
 *   <cxn ang="0">
 *     <pos x="x4" y="y1" />
 *   </cxn>
 * </cxnLst>
 *
 * <rect l="x2" t="it" r="x3" b="y2" xmlns="http://schemas.openxmlformats.org/drawingml/2006/main" />
 *
 * <pathLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *   <path>
 *     <moveTo>
 *       <pt x="x1" y="y1" />
 *     </moveTo>
 *     <lnTo>
 *       <pt x="hc" y="t" />
 *     </lnTo>
 *     <lnTo>
 *       <pt x="x4" y="y1" />
 *     </lnTo>
 *     <lnTo>
 *       <pt x="x3" y="y2" />
 *     </lnTo>
 *     <lnTo>
 *       <pt x="x2" y="y2" />
 *     </lnTo>
 *     <close />
 *   </path>
 * </pathLst>
 *
 * </pentagon>
 */
